import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { map, switchMap } from 'rxjs/internal/operators';
import { forkJoin, of, Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { InternalDocument } from '../../../../../shared/model/internal-document';
import { TaskService } from '../../../../../shared/service/task.service';
import { SignaturePad } from 'angular2-signaturepad';
import { AddressService } from '../../../../shared/services/address.service';
import { CountyDTO } from '../../../../shared/models/CountyDTO';
import { CityDTO } from '../../../../shared/models/CityDTO';
import Swal from 'sweetalert2';
import { DocumentService } from '../../../../../shared/service/document.service';
import { NetworkContractUrl } from 'src/app/shared/model/network-contract-url';
import { UtilService } from 'src/app/shared/service/util.service';

@Component({
  selector: 'app-network-contract-task',
  templateUrl: './network-contract-task.component.html',
  styleUrls: ['./network-contract-task.component.scss']
})
export class NetworkContractTaskComponent implements OnInit, AfterViewInit, OnDestroy {

  public taskForm: FormGroup;
  public taskType: string;
  public fileList: InternalDocument[] = [];
  public urlList: NetworkContractUrl[] = [];
  public routeDataSub: Subscription;
  public editable = false;
  public taskId: number;
  public counties: CountyDTO[] = [];
  public cities: CityDTO[] = [];
  public showSendToFui = false;
  public signaturePadOptions = {
    minWidth: 2,
    canvasHeight: 300
  };
  public signatureCheckBoxText = 'Sunt de acord ca semnatura de mai sus sa fie reprezentarea digitala a propriei semnaturi si sa fie utilizata in toate cazurile cand va fi nevoie sa semnez documentele din cadrul procesului de Contractare. Aceasta semnatura va avea aceeasi valoare ca semnatura olografa, pe hartie.';
  public externalLinksLabel = 'Linkuri externe';

  @ViewChild('signatureDiv') signatureDiv: ElementRef;
  @ViewChild(SignaturePad) signaturePad: SignaturePad;

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this.handleSignatureCanvasSize();
  }

  constructor(
    public documentService: DocumentService,
    private taskService: TaskService,
    private addressService: AddressService,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
  ) {
    this.createTaskForm();
  }

  ngOnInit(): void {
    this.routeDataSub = this.route
      .paramMap
      .pipe(
        map(value => {
          this.editable = !!window.history.state.editable;
          this.taskId = window.history.state.taskId;
          return this.taskId;
        }),
        switchMap(
          taskId => taskId ? this.taskService.getTask(taskId) : of(
            {
              taskNumber: '',
              taskName: 'Contractare serviciu de retea direct CF',
              documentsToSign: [
                {
                  name: 'Contract de retea', size: 120,
                  url: 'http://repo-dev-1:47200/DocumentePersonale/PF/1111111111118/CNP_radu_marcu_20220725100808.png'
                },
                {
                  name: 'Conventie', size: 120,
                  url: 'http://repo-dev-1:47200/DocumentePersonale/PF/1111111111118/CNP_radu_marcu_20220725100808.png'
                }
              ]
            })
        )
      ).subscribe((task: any) => {
        this.taskType = task.type;
        if (task.type === 'ContractNetworkChangedInfoCCS') {
          this.showSendToFui = true;
        }
        this.initializeFormFields(task);
        forkJoin({
          counties: this.addressService.getCounties(),
          cities: this.addressService.getCities(+task.information.place.county)
        }).subscribe(result => {
          if (result.counties) {
            this.counties = result.counties;
            for (const county of result.counties) {
              if (county.id == task.information.place.county) {
                this.taskForm.patchValue({
                  county: county.name
                });
              }
            }
          }
          if (result.cities) {
            this.cities = result.cities;
            for (const city of result.cities) {
              if (city.id == task.information.place.city) {
                this.taskForm.patchValue({
                  city: city.name
                });
              }
            }
          }
        });
      });
  }

  ngAfterViewInit(): void {
    this.handleSignatureCanvasSize();
  }

  handleSignatureCanvasSize(): void {
    const canvas = document.querySelector('canvas');
    if (canvas && this.signatureDiv) {
      const tempWidth = String(this.signatureDiv.nativeElement.clientWidth);
      const widthCanvas = tempWidth.concat('px');
      canvas.setAttribute('width', widthCanvas);
    }
  }

  initializeFormFields(task: any): void {
    console.log('task', task);
    this.taskForm.patchValue({
      number: task.id,
      name: task.name,
      code: task.information.place.code,
      client: task.information.clientName ? task.information.clientName : '',
      clientCode: task.information.clientCode ? task.information.clientCode : '',
      oldSupplier: task.information.oldSupplier ? task.information.oldSupplier.name : '',
      newSupplier: task.information.newSupplier ? task.information.newSupplier.name : '',
      directNetworkContractType: task.information.contractWithOperatorNeeded,
      // county: task.information.place.county
      // city: task.information.place.city
      streetName: task.information.place.streetName,
      streetNumber: task.information.place.streetNumber,
      block: task.information.place.block,
      apartment: task.information.place.apartment,
      floor: task.information.place.floor,
      postalCode: task.information.place.postalCode,
      supplierChangeDate: task.information.startingDate,
      index: task.requestWizard ? task.requestWizard.indexValue : task.information.indexValue,
      indexDate: task.requestWizard ? task.requestWizard.indexDate : task.information.indexDate,
      contractTypeDistribution: task.information.place.networkType,
      contractNumber: task.information.number, // change to network
      contractDate: task.information.date, // change to network
      conventionNumber: task.information.number,
      conventionDate: task.information.date,
      invoiceSentThroughEmail: task.information.invoiceTransmissionMethod === 'Email',
      moneyReturnedThroughBank: task.information.refundingOption === 'Cont bancar',
      ibanBankAccount: task.information.ibanBankAccount,
      reason: task.information.reason,
      reasonDesc: task.information.reasonDesc,
      sendToFUI: task.resolution?.sendToFUI
    });

    for (const field of Object.keys(this.taskForm.controls)) {
      const control = this.taskForm.get(field);
      control.disable();
      if (this.editable && (
        field === 'signatureCheckBox' ||
        field === 'index' ||
        field === 'indexDate'
      )) {
        control.enable();
      }
    }
    this.fileList = task.information.documents;
    this.urlList = task.information.urls;
  }

  createTaskForm(): void {
    this.taskForm = this.formBuilder.group({
      number: [''],
      name: [''],
      code: [''],
      client: [''],
      clientCode: [''],
      oldSupplier: [''],
      newSupplier: [''],
      directNetworkContractType: [''],
      //  address
      county: [''],
      city: [''],
      village: [''],
      streetName: [''],
      streetNumber: [''],
      block: [''],
      apartment: [''],
      floor: [''],
      postalCode: [''],

      supplierChangeDate: [''],
      index: ['', UtilService.counterIndexValueValidator],
      indexDate: [''],
      contractTypeDistribution: [''],
      contractNumber: [''],
      contractDate: [''],
      conventionPresent: [''],
      conventionTypeMultiple: [''],
      conventionNumber: [''],
      conventionDate: [''],
      invoiceSentThroughEmail: [''],
      moneyReturnedThroughBank: [''],
      ibanBankAccount: [''],
      signatureCheckBox: [''],
      sendToFUI: [''],
      reason: [''],
      reasonDesc: [''],
    });
  }

  isSupplierNetworkContractType(): boolean {
    return !this.taskForm.get('directNetworkContractType').value;
  }

  isDirectNetworkContractType(): boolean {
    return !!this.taskForm.get('directNetworkContractType').value;
  }

  isContractTypeDistribution(): boolean {
    return this.taskForm.get('contractTypeDistribution').value === 'D';
  }

  isContractTypeTransport(): boolean {
    return this.taskForm.get('contractTypeDistribution').value === 'T';
  }

  isContractTypeAccess(): boolean {
    return this.taskForm.get('contractTypeDistribution').value === 'CA';
  }

  isConventionPresent(): boolean {
    return !!this.taskForm.get('conventionPresent').value;
  }

  isConventionAbsent(): boolean {
    return !this.taskForm.get('conventionPresent').value;
  }

  isConventionTypeMultiple(): boolean {
    return !!this.taskForm.get('conventionTypeMultiple').value;
  }

  isConventionTypeTriple(): boolean {
    return !this.taskForm.get('conventionTypeMultiple').value;
  }

  isInvoiceSentThroughEmail(): boolean {
    return !!this.taskForm.get('invoiceSentThroughEmail').value;
  }

  isInvoiceSentThroughMail(): boolean {
    return !this.taskForm.get('invoiceSentThroughEmail').value;
  }

  isMoneyReturnedThroughBank(): boolean {
    return !!this.taskForm.get('moneyReturnedThroughBank').value;
  }

  isMoneyReturnedThroughPost(): boolean {
    return !this.taskForm.get('moneyReturnedThroughBank').value;
  }

  onSubmit(): void {
    if (!this.taskForm.valid && this.showSendToFui && this.taskForm.get('sendToFUI').value === '') {
      this.errorNotification('Nu toate campurile marcate cu steluta rosie au fost completate. Va rugam verificati');
      return;
    }

    this.taskService.resolveTaskOperatorSemnatura(this.taskId, {
      indexValue: this.taskForm.get('index').value,
      indexDate: this.taskForm.get('indexDate').value,
      signature: this.signaturePad.toDataURL(),
      sendToFUI: this.taskForm.get('sendToFUI').value,
      reason: this.taskForm.get('reason').value
    })
      .subscribe(() => {
        history.back();
      });
    /*this.taskService.resolveTask(this.taskId, this.taskForm.getRawValue()).subscribe({
      next: () => {
        this.toastr.success('Task rezolvat.');
        history.back();
      },
      error: () => this.toastr.error('Acest task nu a putut fi rezolvat.')
  });*/
  }

  ngOnDestroy(): void {
    if (this.routeDataSub) {
      this.routeDataSub.unsubscribe();
    }
  }

  setSendToFUI(value: boolean): void {
    this.taskForm.controls['sendToFUI'].setValue(value);
  }

  isSentToFUI(): boolean {
    return this.taskForm.get('sendToFUI').value === true;
  }

  isNotSentToFUI(): boolean {
    return this.taskForm.get('sendToFUI').value === false;
  }

  errorNotification(message: string): void {
    Swal.fire({
      title: ' ',
      text: message,
      customClass: {
        popup: 'popup-z-elevate-24-blue'
      }
    });
  }
}
